/**
 * Filter relaxation for "Recommended properties beyond your filters" (P1D-5464, P1D-5538).
 * Pure functions only — the thunks in `data/recommendationsThunks.ts` do the I/O.
 * Relative imports only, so the bare jest config can load this module.
 */
import { MOVE_IN_TOKEN, appliedGroups, groupRank, tokenKey } from "./filterTokens";
import {
  MOVE_IN_MONTH_KEY,
  MOVE_IN_MONTH_WINDOW_KEY,
  withWidenedMonth,
  widenedMonthParam,
} from "./moveInMonthWindow";

export const RECOMMENDATIONS_PAGE_SIZE = 10;
/** Most recommendation cards the section ever shows; paging stops once it is reached. */
export const RECOMMENDATIONS_MAX_ITEMS = 15;
/** A candidate is accepted only when it adds at least this many new bookable properties. */
export const MIN_NEW_BOOKABLE = 5;

export type RelaxationSortKey = "relevance_sorting_v2";

/** One cumulative relaxation candidate (HLD §3 step 2). */
export type ChangeSet = {
  monthWidened: boolean;
  /** Group ids (filter keys, e.g. "meta_types"), in removal order, cumulative. */
  removedGroups: string[];
};

/** C_0: the main request, nothing changed. */
export const MAIN_REQUEST: ChangeSet = { monthWidened: false, removedGroups: [] };

export type RelaxationStep = ChangeSet & {
  /** 1-based position in the plan; `step_index` in events and `stepOfItem` values. */
  index: number;
  /** Bookable: N_i − N_{i−1}. Sold-out tail: count(C_k, available=false). */
  pool: number;
  sortKey: RelaxationSortKey;
  /** The tail step: the stop step's sold-out properties, shown last. */
  soldOut?: boolean;
  /** Change set negated in `not_filter`: C_{i−1} (C_0 = MAIN_REQUEST). null = none (sold-out tail). */
  negate: ChangeSet | null;
};

export type RecommendationSummary = {
  /** |G|: relaxable groups applied (Availability excluded). */
  appliedGroupCount: number;
  /** k: candidates run up to the stop step, incl. the widen and zero-pool steps. */
  stepCount: number;
  /**
   * The section shows properties from a widened-month step: a bookable step with a pool widens
   * the month (2026-10-22). With the month as the only filter this stays true after the month is
   * removed at C_k, as long as the widen step added at least one property.
   */
  monthWidened: boolean;
  /** C_k's removed groups, in removal order. */
  removedGroups: string[];
};

/**
 * Cumulative change sets C_1 … C_m: widen the month first (when applied), then remove whole
 * groups most recently applied first, Move in Month always last, at most |G| − 1 removals
 * (one when a single group is applied) — the n + 1 rule.
 */
export const buildRelaxationSteps = (
  tokens: string[] = [],
  applicationOrder: string[] = [],
): ChangeSet[] => {
  const groups = appliedGroups(tokens);
  if (!groups.length) return [];
  const allowed = groups.length === 1 ? 1 : groups.length - 1;
  const rank = (g: string) => groupRank(g, tokens, applicationOrder);
  const byRecency = [...groups].sort((a, b) => rank(b) - rank(a));
  const order = [
    ...byRecency.filter((g) => g !== MOVE_IN_TOKEN),
    ...byRecency.filter((g) => g === MOVE_IN_TOKEN),
  ];
  const removals = order.slice(0, allowed);
  const monthApplied = groups.includes(MOVE_IN_TOKEN);
  const steps: ChangeSet[] = monthApplied ? [{ monthWidened: true, removedGroups: [] }] : [];
  for (let i = 1; i <= removals.length; i += 1) {
    const removedGroups = removals.slice(0, i);
    steps.push({
      monthWidened: monthApplied && !removedGroups.includes(MOVE_IN_TOKEN),
      removedGroups,
    });
  }
  return steps;
};

/** Request keys the main listing sends for each group (the group id itself otherwise). */
const REMOVE_KEYS: Record<string, string[]> = {
  range_available_price: ["range_available_price", "weekly_range_price", "duration"],
  // Removing the month also removes its widened window (P1D-5529), so `not_filter` carries both.
  move_in_month: ["move_in_month", MOVE_IN_MONTH_WINDOW_KEY],
  leaseDuration: ["meta_min_lease_duration", "meta_max_lease_duration"],
  meta_types: ["room_types", "bathroom_types", "kitchen_types"],
  guarantor: ["guarantor"],
  tags: ["tags"],
  meta_unit_types: ["meta_unit_types"],
  // P1D-5528 (K-9): relaxed as one filter (AND across its options), like guarantor.
  cancellation_policy: ["cancellation_policy"],
};

const AVAILABLE_KEY = "available";

/** Whether the main listing already excluded sold-out properties. */
export const excludesSoldOut = (base: Record<string, any> = {}) =>
  String(base[AVAILABLE_KEY]) === "true";

/** Keys that are never copied from the main request — the caller sets its own. */
const ALWAYS_DROP = ["p", "limit", "sort_key", "sort_order", "ids", "not_ids", "not_filter"];

/**
 * Request keys a standard filter can occupy — the only keys ever sent inside `not_filter`.
 * `available` is absent: it is a positive constraint on every step, negating it empties the page.
 */
const STANDARD_REQUEST_KEYS = [
  ...new Set([...Object.values(REMOVE_KEYS).flat(), "amenities", "bills_included"]),
];

/**
 * Copy the main listing request's params with a change set applied: whole groups removed,
 * then the month widened. Deriving from the request actually sent (not from filter state)
 * keeps counts and pages consistent.
 */
export const relaxParams = (
  base: Record<string, any> = {},
  changeSet: ChangeSet = MAIN_REQUEST,
  { soldOut = false }: { soldOut?: boolean } = {},
) => {
  let params: Record<string, any> = { ...base };
  ALWAYS_DROP.forEach((k) => delete params[k]);
  (changeSet?.removedGroups || []).forEach((group) => {
    (REMOVE_KEYS[group] || [group]).forEach((k) => delete params[k]);
  });
  if (changeSet?.monthWidened) params = withWidenedMonth(params);
  // Available steps only show bookable properties; the sold-out tail step only sold-out ones.
  params[AVAILABLE_KEY] = !soldOut;
  return params;
};

/**
 * `not_filter` value for a step: the standard-filter params of the change set it negates
 * (the previous candidate, or the main request), as a query string. java-backend negates that
 * whole set as one clause, so the page is exactly the properties the user has not seen.
 */
export const negationParams = (
  base: Record<string, any> = {},
  changeSet: ChangeSet = MAIN_REQUEST,
) => {
  const relaxed = relaxParams(base, changeSet);
  const qs = new URLSearchParams();
  STANDARD_REQUEST_KEYS.forEach((key) => {
    const value = relaxed[key];
    if (value !== undefined && value !== null && String(value) !== "") qs.set(key, String(value));
  });
  return qs.toString();
};

/**
 * `meta.count` of a count response. A missing, non-numeric or negative count throws an error
 * with `code: "invalid_count"` (contract §8.1, P1D-5538 TC-I09): planning then fails, the section
 * hides and `search_recommendations_failed` reports it, instead of passing for "fewer than 5".
 */
export const parseCount = (data: any): number => {
  const raw = data?.data?.meta?.count;
  const n = raw === null || raw === undefined || raw === "" ? NaN : Number(raw);
  if (Number.isFinite(n) && n >= 0) return n;
  throw Object.assign(new Error("invalid_count"), { code: "invalid_count" });
};

/** Index (0-based) of the first candidate with ≥ 5 new bookable properties, or -1. */
export const findStopStep = (stepCounts: number[], baseline: number) =>
  stepCounts.findIndex((c) => c - baseline >= MIN_NEW_BOOKABLE);

type PlanInput = {
  changeSets: ChangeSet[];
  stepCounts: number[];
  baseline: number;
  stop: number;
  soldOutCount: number;
  appliedGroupCount: number;
};

/** The plan (paged steps with a pool, then the sold-out tail) and the copy/event summary. */
export const planFromCounts = ({
  changeSets,
  stepCounts,
  baseline,
  stop,
  soldOutCount,
  appliedGroupCount,
}: PlanInput): { plan: RelaxationStep[]; summary: RecommendationSummary } => {
  const plan: RelaxationStep[] = [];
  let prevNew = 0;
  for (let i = 0; i <= stop; i += 1) {
    const newBookable = Math.max(0, stepCounts[i] - baseline);
    // Running max: a live count that drops between candidates gives pool 0, never negative.
    const pool = Math.max(0, newBookable - prevNew);
    prevNew = Math.max(prevNew, newBookable);
    if (pool > 0) {
      plan.push({
        ...changeSets[i],
        index: plan.length + 1,
        pool,
        sortKey: "relevance_sorting_v2",
        // The immediately previous candidate, even when it had pool 0 and is not in the plan.
        negate: i === 0 ? MAIN_REQUEST : changeSets[i - 1],
      });
    }
  }
  const tail = Number.isFinite(soldOutCount) ? Math.max(0, soldOutCount) : 0;
  if (tail > 0) {
    plan.push({
      ...changeSets[stop],
      index: plan.length + 1,
      pool: tail,
      sortKey: "relevance_sorting_v2",
      soldOut: true,
      negate: null,
    });
  }
  const summary: RecommendationSummary = {
    appliedGroupCount,
    stepCount: stop + 1,
    monthWidened: plan.some((step) => !step.soldOut && step.monthWidened),
    removedGroups: changeSets[stop].removedGroups,
  };
  return { plan, summary };
};

/** Property-page link keys each group occupies (`getPropertyFilter`); others pass none. */
const PROPERTY_LINK_KEYS_BY_GROUP: Record<string, string[]> = {
  range_available_price: ["range_available_price"],
  move_in_month: [MOVE_IN_MONTH_KEY, MOVE_IN_MONTH_WINDOW_KEY],
  leaseDuration: ["meta_min_lease_duration", "meta_max_lease_duration"],
  meta_types: ["meta_types"],
  tags: ["tags"],
};

export type RecommendationLinkContext = {
  /** Group ids removed cumulatively in the card's step. */
  removed_filter_groups?: string[];
  month_widened?: boolean;
};

/**
 * Event / attribution `removed_filters` (pre-P1D-5538 shape): the applied option tokens whose
 * group is in `removedGroups`, in the groups' removal order. `removed_filter_groups` is the
 * group ids themselves.
 */
export const removedFilterFields = (
  appliedTokens: string[] = [],
  removedGroups: string[] = [],
) => ({
  removed_filters: removedGroups.flatMap((group) =>
    appliedTokens.filter((token) => tokenKey(token) === group),
  ),
  removed_filter_groups: [...removedGroups],
});

/**
 * Property-page link params for a card. Main cards (no `rec`) get `params` unchanged; a
 * recommended card drops its step's removed groups and adds the widened window when its step
 * widens the month and the month is still in the link. Non-filter params pass through.
 */
export const recommendedPropertyParams = <T extends Record<string, any>>(
  params: T,
  rec?: RecommendationLinkContext | null,
): T => {
  if (!rec) return params;
  const next: Record<string, any> = { ...params };
  (rec.removed_filter_groups || []).forEach((group) =>
    (PROPERTY_LINK_KEYS_BY_GROUP[group] || []).forEach((k) => delete next[k]),
  );
  // The property page reads "nov-2026,oct-2026,dec-2026" as November widened to ±1.
  const widened =
    rec.month_widened && next[MOVE_IN_MONTH_KEY]
      ? widenedMonthParam(next[MOVE_IN_MONTH_KEY])
      : null;
  if (widened) next[MOVE_IN_MONTH_KEY] = widened;
  return next as T;
};

/** The plan step a recommended card belongs to, or undefined (no fallback: main pins). */
export const stepForItem = (
  plan: RelaxationStep[] = [],
  stepOfItem: Record<string, number> = {},
  id: string | number | undefined | null,
): RelaxationStep | undefined => {
  if (id === undefined || id === null) return undefined;
  const index = stepOfItem[String(id)];
  if (index === undefined) return undefined;
  return plan.find((s) => s.index === index);
};
